import { RxMeetingsAPI, RxConversationAPI, RxMessagingAPI, RxCustomerAPI } from '@driftt/dapper'
import { List, Set } from 'immutable'
import { Observable } from 'rxjs/Observable'
import { Calendar, makeMeeting, makeMeetingTypes, processCalendlyMeetings } from './models'
import { MeetingSource } from './constants'

export const fetchCalendarStatus = () => {
  return RxMeetingsAPI.get('/scheduling/settings/status')
    .checkStatus()
    .retryWithBackoff()
    .map(({ response }) => {
      return {
        connected: response.connected,
        calendar: Calendar(response.calendar),
        googleAuthed: response.googleAuthed,
        microsoftAuthed: response.microsoftAuthed,
        isTokenRevoked: response.tokenRevoked,
      }
    })
}

export const fetchGoogleCalendars = () => {
  return RxMeetingsAPI.get('/google/calendar/calendars')
    .checkStatus()
    .retryWithBackoff()
    .map(({ response }) => {
      return List(response).map(calendar => Calendar(calendar))
    })
}

export const fetchGoogleScopes = () => {
  return RxCustomerAPI.get('/google/calendar/oauth/scopes')
    .checkStatus()
    .retryWithBackoff()
    .mapResponse(response => response)
}

export const fetchMicrosoftCalendars = () => {
  return RxMeetingsAPI.get('/microsoft/calendar/calendars')
    .checkStatus()
    .retryWithBackoff()
    .map(({ response }) => {
      return List(response).map(calendar => Calendar(calendar))
    })
}

export const fetchMicrosoftAdminConsent = () => {
  return RxMeetingsAPI.get('/microsoft/calendar/isManaged')
    .checkStatus()
    .mapResponse(response => response)
}

export const microsoftAdminConsentAccept = (tenant) => {
  return RxCustomerAPI.post('/microsoft/calendar/managed/accept', tenant)
    .checkStatus()
    .mapResponse(response => response)
}

export const fetchMicrosoftManagedCalendars = () => {
  return RxMeetingsAPI.get('/microsoft/calendar/managed/calendars/choose')
    .checkStatus()
    .retryWithBackoff()
    .mapResponse(response => response)
}

export const disconnectMicrosoftAdminConsent = () => {
  return RxMeetingsAPI.post('/microsoft/calendar/managed/disconnect').checkStatus()
}

export const updateGoogleCalendar = calendar => {
  return RxMeetingsAPI.post('/google/calendar/calendars/choose', calendar).checkStatus()
}

export const updateMicrosoftCalendar = calendar => {
  return RxMeetingsAPI.post('/microsoft/calendar/calendars/choose', calendar).checkStatus()
}

export const disconnectGoogleCalendar = () => {
  return RxMeetingsAPI.post('/google/calendar/disconnect').checkStatus()
}

export const disconnectMicrosoftCalendar = () => {
  return RxMeetingsAPI.post('/microsoft/calendar/disconnect').checkStatus()
}

export const googleOauthAccept = oauthCode => {
  return RxCustomerAPI.post('/google/calendar/oauth/accept', {
    code: oauthCode,
  }).checkStatus()
}

export const microsoftOauthAccept = oauthCode =>
RxCustomerAPI.post('/microsoft/calendar/oauth/accept', { code: oauthCode }).checkStatus()

export const fetchMeetingSettings = () => {
  return RxMeetingsAPI.get('/scheduling/settings')
    .checkStatus()
    .retryWithBackoff()
    .map(({ response }) => makeMeeting(response))
}

export const fetchMeetingTypes = () => {
  return RxMeetingsAPI.get('/scheduling/settings/custom')
    .checkStatus()
    .retryWithBackoff()
    .map(({ response }) => makeMeetingTypes(response))
}

export const fetchCalendlyMeetingTypes = apiKey => {
  return Observable.ajax({
    url: `https://calendly.com/api/v1/users/me/event_types`,
    method: 'GET',
    crossDomain: true,
    responseType: 'json',
    headers: { 'X-TOKEN': apiKey },
  }).map(res => {
    const meetingTypes = res.response.data
    return processCalendlyMeetings(meetingTypes)
  })
}

export const updateMeetingSettings = meeting => {
  const queryPayload = meeting.oldSlug ? `?oldSlug=${meeting.oldSlug}` : ''
  return RxMeetingsAPI.put(
    `/scheduling/settings${meeting.slug ? `/custom/${meeting.slug}` : ''}${queryPayload}`,
    {
      allowNewAppointments: meeting.allowNewAppointments,
      allowMeetingManagement: meeting.allowMeetingManagement,
      bufferTime: meeting.bufferTime,
      minimumNoticeTime: meeting.minimumNoticeTime,
      timezone: meeting.timezone,
      availability: meeting.availability.map(slot => {
        return {
          dayOfWeek: slot.dayOfWeek,
          startTimeHour: slot.startTime.hour,
          startTimeMinutes: slot.startTime.minutes,
          endTimeHour: slot.endTime.hour,
          endTimeMinutes: slot.endTime.minutes,
        }
      }),
      meetingTemplate: {
        duration: meeting.duration,
        name: meeting.name,
        location: meeting.location,
        inviteDescription: meeting.inviteDescription,
      },
    }
  )
    .checkStatus()
    .map(({ response }) => makeMeeting(response))
}

export const deleteMeetingType = slug => {
  return RxMeetingsAPI.delete(`/scheduling/settings/custom/${slug}`).checkStatus()
}

export const updateMeetingSettingsNewAppointments = allowNewAppointments => {
  return RxMeetingsAPI.post(`/scheduling/settings`, {
    allowNewAppointments,
  })
    .checkStatus()
    .map(({ response }) => makeMeeting(response).allowNewAppointments)
}

export const sendShareCalendarMessage = ({ agentId, conversationId, meetingSource = MeetingSource.NONE }) => {
  return RxMessagingAPI.post(`/conversations/${conversationId}/messages`, {
    type: 'CHAT',
    attributes: {
      offerSchedule: agentId,
      meetingSource: meetingSource,
    },
  }).checkStatus()
}

export const fetchConnectedCalendars = () => {
  return RxMeetingsAPI.get(`/scheduling/settings/connected`)
    .checkStatus()
    .map(({ response }) => Set(response))
}

export const sendShareRoutedCalendarMessage = ({ conversationId }) => {
  return RxConversationAPI.post(`/routed-calendar-drop?conversationId=${conversationId}`, {}).checkStatus()
}



// WEBPACK FOOTER //
// ./src/scripts/app/modules/meetings/api.js